import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ExternalLink, CheckCircle } from "lucide-react";

interface Props {
  detectedOAuthUrl?: string | null;
  isCompletingSetup?: boolean;
  errorMessage?: string | null;
  oauthToken: string;
  setupStatus:
    | "idle"
    | "terminal-connecting"
    | "executing-command"
    | "waiting-for-auth"
    | "submitting-code"
    | "closing-terminal"
    | "saving-token";
  onOauthTokenChange: (value: string) => void;
  onSubmitCode: () => void;
}

const OAuthAuthStep: React.FC<Props> = ({
  detectedOAuthUrl,
  isCompletingSetup = false,
  errorMessage,
  oauthToken,
  setupStatus,
  onOauthTokenChange,
  onSubmitCode,
}) => {
  return (
    <div className="space-y-4">
      <div className="text-sm text-secondary-n-300">
        Complete the OAuth authentication process.
      </div>

      {detectedOAuthUrl && (
        <div className="bg-primary-n-950/20 border border-primary-n-800 p-4 rounded-md">
          <div className="text-sm text-secondary-n-200 mb-3">
            <strong>Step 1: Authenticate with Claude.ai</strong>
          </div>
          <div className="text-sm text-secondary-n-300 mb-3">
            Click the button below to open Claude.ai and complete the
            authentication:
          </div>
          <Button
            onClick={() => window.open(detectedOAuthUrl, "_blank")}
            className="w-full mb-3"
            variant="primary"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open Claude.ai Authentication
          </Button>
        </div>
      )}

      <div className="bg-secondary-n-900/50 p-4 rounded-md">
        <div className="text-sm text-secondary-n-200 mb-3">
          <strong>Step 2: Enter Authorization Code</strong>
        </div>
        <div className="text-sm text-secondary-n-300 mb-3">
          After authenticating in your browser, you'll receive an authorization
          code. Paste it here:
        </div>
        <div className="space-y-2">
          <Label htmlFor="auth-code" className="text-sm">
            Authorization Code
          </Label>
          <Input
            id="auth-code"
            type="text"
            placeholder="Paste the authorization code here..."
            value={oauthToken}
            onChange={(e) => onOauthTokenChange(e.target.value)}
            className="font-mono text-xs placeholder:text-xs"
          />
          <Button
            onClick={onSubmitCode}
            disabled={!oauthToken.trim() || setupStatus === "submitting-code"}
            className="w-full"
            variant="primary"
          >
            {setupStatus === "submitting-code" && (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            )}
            {setupStatus === "submitting-code"
              ? "Submitting..."
              : "Submit Code"}
          </Button>
        </div>
      </div>

      {errorMessage && (
        <div className="bg-red-900/20 border border-red-600 p-4 rounded-md">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center">
              <span className="text-white text-xs">!</span>
            </div>
            <div className="text-sm text-red-200 font-medium">
              Authorization Failed
            </div>
          </div>
          <div className="text-sm text-red-300 mb-3">{errorMessage}</div>
          <div className="text-2xs text-red-400">
            Having trouble? You can also{" "}
            <a
              href="https://tempolabsinc.mintlify.app/Claude%20Code/claude-code-integration#max-subscription-recommended"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline"
            >
              set up Claude Code manually
            </a>{" "}
            following our step-by-step guide.
          </div>
        </div>
      )}

      {isCompletingSetup && (
        <div className="bg-blue-900/20 border border-blue-600 p-4 rounded-md">
          <div className="flex items-center gap-3 mb-3">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <div className="text-sm text-blue-200 font-medium">
              Completing Setup...
            </div>
          </div>
          <div className="text-xs text-blue-300">
            Finalizing authentication and configuring Claude Code. This will
            take just a moment.
          </div>
        </div>
      )}

      <div className="text-xs text-secondary-n-400 bg-secondary-n-900/50 p-3 rounded-md text-center">
        To use Anthropic API key (prepaid), follow instructions{" "}
        <a
          href="https://tempolabsinc.mintlify.app/Claude%20Code/claude-code-integration#anthropic-api-method"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-n-500 hover:text-primary-n-400 underline"
        >
          here
        </a>
      </div>
    </div>
  );
};

export default OAuthAuthStep;
